import {
  Link,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_PROMO_CODE } from "./queries";
import { Discount } from "../../types/discount";
import { usdFormat } from "../../utils";
import { useEffect, useRef, useState } from "react";
import { Badge } from "../../components/Badge";
import moment from "moment/moment";
import { usePermissions } from "../auth/api";
import { organizationStore } from "../../store/organization";
import { eventStore } from "../../store/event";
import {
  ColDef,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Input } from "../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { PromoCode } from "../../types/promoCode";
import {userStore} from "../../store/user";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventStore } = eventStore;

const buildSearch = (search: string) => [
  {
    promo_code_type: {
      _ilike: `%${search}%`,
    },
  },
  {
    description: {
      _ilike: `%${search}%`,
    },
  },
  {
    uid: {
      _ilike: `%${search}%`,
    },
  },
  {
    email: {
      _ilike: `%${search}%`,
    },
  },
  {
    phone_number: {
      _ilike: `%${search}%`,
    },
  },
];

type CellRenderer = (params: {
  value:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  data: PromoCode;
}) => JSX.Element | null;
const createCellRenderer = (
  permission: boolean,
  linkFunc: (params: { data: PromoCode }) => string
): CellRenderer => {
  return (params) => {
    if (params.value === undefined) {
      return null;
    }
    return (
      <>
        {permission ? (
          <Link to={linkFunc(params)}>
            <Badge>{params.value}</Badge>
          </Link>
        ) : (
          <Badge>{params.value}</Badge>
        )}
      </>
    );
  };
};

export const promoCodeListLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const search = url.searchParams.get("search");
  const rawOrganizationId = getOrganizationState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
  const eventId = getEventStore().eventId;
  const user = getState().user;
  console.log(organizationId);

  let where: any = { _or: search ? buildSearch(search) : undefined };
  if (organizationId && organizationId !== 0) {
    where = {
      ...where,
      organization_id: {
          _eq: organizationId,
      },
    };
  }
  if (eventId) {
    where = {
      ...where,
      event_id: {
        _eq: eventId,
      },
    };
  } else if (user?.events) {
    where = {
      ...where,
      event_id: {
        _in: user?.events,
      },
    };
  }
  const { data } = await client.query({
    query: GET_PROMO_CODE,
    variables: {
      where,
    },
  });
  return data.promo_code;
};

export const PromoCodeList = () => {
  const { editCreatePermission } = usePermissions("promocode");
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const gridRef = useRef<GridApi>();
  const discounts = useLoaderData() as PromoCode[];
  const [rowData, setRowData] = useState<PromoCode[]>([]);
  useEffect(() => {
    setRowData(discounts);
  }, [discounts]);
  const generateColDefs = (): ColDef<PromoCode>[] => [
    {
      headerName: "Promo Code",
      valueGetter: (p) => p.data?.uid,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/promocode/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Is Used",
      valueGetter: (p) => (p.data?.is_used ? "Yes" : "No"),
      filter: "agTextColumnFilter",
    },

    {
      headerName: "Unlimited Usage",
      valueGetter: (p) => (p.data?.unlimited_usage ? "Yes" : "No"),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Balance",
      valueGetter: (p) => p.data?.balance,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Promo Code Type",
      valueGetter: (p) =>
        p.data?.promo_payload?.promo_code_value_type === "percentage_off"
          ? "Percentage Off"
          : "Amount Off",
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Description",
      valueGetter: (p) => p.data?.description,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Event Name",
      valueGetter: (p) => p.data?.event?.name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Amount",
      valueGetter: (p) => p.data?.promo_payload?.amount,
      valueFormatter: (params) =>
        params.value ? usdFormat.format(params.value / 100 ?? 0) : "-",
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.promo_payload?.amount
          ? usdFormat.format(p.data.promo_payload.amount / 100)
          : "-",
    },
    {
      headerName: "Percentage",
      valueGetter: (p) => p.data?.promo_payload?.percentage,
      valueFormatter: (p) => {
        return p.value ? `${((p.value || 0) * 100).toFixed(2)}%` : "";

      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) => {
        const n = p.data?.promo_payload?.percentage;
        return n ? `${(n * 100).toFixed(2)}%` : "";
      },
    },
    {
      headerName: "Last Sent Email",
      valueGetter: (p) => p.data?.email,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Last Sent SMS",
      valueGetter: (p) => p.data?.phone_number,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Attendee",
      valueGetter: (p) => {
        const firstName = p.data?.attendee?.first_name;
        const lastName = p.data?.attendee?.last_name;
        if (firstName === undefined || lastName === undefined) {
          return undefined;
        }
        return `${firstName} ${lastName}`.trim();
      },
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/attendees/${p.data.attendee?.id}/edit`
      ),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Updated By",
      valueGetter: (p) => p.data?.user?.username,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Created Date",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
    {
      headerName: "Updated Date",
      valueGetter: (p) => p.data?.updated_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
      filter: "agTextColumnFilter",
      filterValueGetter: (p) =>
        p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
    },
  ];
  const colDefs: ColDef<PromoCode>[] = generateColDefs();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `PromoCode_${timestamp}.csv`,
        columnSeparator: ",",
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <div style={{ height: "75vh" }}>
      <div
        className={"ag-theme-quartz"}
        style={{ width: "100%", height: "100%" }}
      >
        <div className=" grid gap-2 grid-cols-6">
          <Breadcrumbs />
          <div className="col-span-2 col-start-5 mt-2">
            <Input
              name="search"
              placeholder={`Search Promo Code`}
              onChange={(s) => {
                navigate(
                  s && typeof s === "string" && s.length > 0
                    ? `/promocode/list?search=${s}`
                    : `/promocode/list`
                );
                revalidator.revalidate();
              }}
            />
          </div>
          <div className="py-2 col-span-2 col-start-7">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
            {editCreatePermission && (
              <PrimaryButton onClick={() => navigate(`/promocode/create`)}>
                Create
              </PrimaryButton>
            )}
          </div>
        </div>
        <AgGridReact
          rowData={rowData}
          columnDefs={colDefs}
          rowHeight={rowHeight}
          autoSizeStrategy={autoSizeStrategy}
          pagination={true}
          gridOptions={gridOptions}
          paginationPageSize={100}
          onGridReady={(params) => (gridRef.current = params.api)}
        />
      </div>
    </div>
  );
};
